<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { relativeDayTimestamp } from 'shared/helpers/timeHelper';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import CallStatusBadge from './CallStatusBadge.vue';
import { CALL_KIND, getCallKind } from './constants';

const props = defineProps({
  call: {
    type: Object,
    required: true,
  },
  isSelected: {
    type: Boolean,
    default: false,
  },
});

const { t } = useI18n();

const kind = computed(() => getCallKind(props.call));

const contactName = computed(() =>
  (props.call.contact.name || props.call.contact.phoneNumber || '').replace(
    /^\+/,
    ''
  )
);

const durationLabel = computed(() => {
  const seconds = props.call.durationSeconds;
  if (!seconds) return '';
  const minutes = Math.floor(seconds / 60);
  return `${minutes}:${String(seconds % 60).padStart(2, '0')}`;
});

// The secondary line answers "what happened" in as few words as possible —
// a duration when the call connected, the reason when it didn't.
const summaryLabel = computed(() => {
  if (kind.value === CALL_KIND.MISSED) return t('CALLS_PAGE.ROW.NO_AGENT');
  if (kind.value === CALL_KIND.NO_REPLY) {
    return t('CALLS_PAGE.ROW.NO_CONTACT_ANSWER');
  }
  if (kind.value === CALL_KIND.FAILED) return t('CALLS_PAGE.ROW.FAILED');
  if (kind.value === CALL_KIND.ONGOING) return t('CALLS_PAGE.ROW.IN_PROGRESS');
  return [durationLabel.value, props.call.agent?.name]
    .filter(Boolean)
    .join(' · ');
});

const createdAtLabel = computed(() =>
  relativeDayTimestamp(props.call.createdAt, t('CALLS_PAGE.ROW.YESTERDAY'))
);
</script>

<template>
  <button
    type="button"
    class="flex items-start w-full gap-2.5 px-4 py-3 text-start border-b border-n-weak hover:bg-n-alpha-1"
    :class="
      isSelected ? 'bg-n-alpha-2 shadow-[inset_2px_0_0_0] shadow-n-brand' : ''
    "
    :aria-current="isSelected ? 'true' : undefined"
  >
    <Avatar
      :src="call.contact.avatar"
      :name="contactName"
      :size="28"
      rounded-full
    />
    <span class="flex flex-col min-w-0 gap-1 grow">
      <span class="flex items-baseline gap-2 min-w-0">
        <span class="font-medium truncate text-heading-3 text-n-slate-12">
          {{ contactName }}
        </span>
        <span
          class="ms-auto shrink-0 text-label-small text-n-slate-10 tabular-nums"
        >
          {{ createdAtLabel }}
        </span>
      </span>
      <span class="flex items-center gap-2 min-w-0">
        <CallStatusBadge :kind="kind" class="shrink-0" />
        <span class="truncate text-label-small text-n-slate-11">
          {{ summaryLabel }}
        </span>
      </span>
    </span>
  </button>
</template>
